import { useEffect, useState } from 'react';

/**
 * Hash routing, in about thirty lines.
 *
 * The app needs deep-linkable sub-pages ("CEO View > Competition") and nothing
 * else a router provides — no nested layouts, no loaders, no code-split
 * boundaries. react-router would be ~20 KB and a dependency to keep current
 * for a feature this small. Hash routes also need no server rewrite rule, so a
 * refresh on /ceo/competition cannot 404 against nginx.
 */
export interface Route {
  section: string;
  page: string;
}

/**
 * The landing surface. Availability used to be the default; it is no longer in
 * the nav (see VISIBLE_SECTIONS in Sidebar), so landing there would open the
 * app on a page with no way back to it.
 */
export const DEFAULT_ROUTE: Route = { section: 'ceo', page: 'market-position' };

function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  if (parts.length === 0) return DEFAULT_ROUTE;
  return { section: parts[0] ?? DEFAULT_ROUTE.section, page: parts[1] ?? '' };
}

export function toHash(route: Route): string {
  return `#/${route.section}${route.page ? `/${route.page}` : ''}`;
}

export function useRoute(): [Route, (next: Route) => void] {
  const [route, setRoute] = useState<Route>(() =>
    parse(typeof window === 'undefined' ? '' : window.location.hash),
  );

  useEffect(() => {
    const onHash = (): void => { setRoute(parse(window.location.hash)); };
    window.addEventListener('hashchange', onHash);
    return () => { window.removeEventListener('hashchange', onHash); };
  }, []);

  const navigate = (next: Route): void => {
    window.location.hash = toHash(next);
    // hashchange does not fire when the hash is unchanged, so set state too —
    // otherwise re-selecting the current page appears to do nothing.
    setRoute(next);
  };

  return [route, navigate];
}
